import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  type Attempt,
  type SessionResult,
  SessionRecorder,
  recordAttempt,
  pickWeightedKey,
  statsFor,
} from "@nav-games/games-core";
import { NAV_LIGHTS, VESSEL_IDS } from "@nav-games/nav-lights";

import { DayShapeFigure } from "../../ui/DayShapeFigure";
import { HelpButton, HelpPanel } from "../../ui/HelpPanel";

interface FlashcardsProps {
  onNavigate: (to: string) => void;
}

const MODULE = "day-shapes";
const GAME = "flashcards";
const CARDS_PER_SESSION = 10;
const BEAT_THE_CLOCK_MS = 90_000;
const CHOICES_PER_CARD = 4;

// Pool: every vessel that actually carries a day shape. Pilots,
// power-driven, hovercraft, sailing-only have no shape — including
// them as the "right answer" would be confusing.
const POOL = VESSEL_IDS.filter((id) => NAV_LIGHTS[id]!.dayShapes.length > 0);

// Recent answers kept out of the next draw so the spaced-repetition
// picker doesn't resurface the same card within a short window.
// Clamped to pool size - 1 so there is always an eligible card.
const AVOID_WINDOW = Math.min(8, POOL.length - 1);

type SessionMode = "cards" | "timed";

interface Card {
  answer: string;
  choices: readonly string[];
}

function nextCard(recent: readonly string[]): Card {
  const answer = pickWeightedKey(MODULE, POOL, recent);
  const distractors = new Set<string>();
  while (distractors.size < CHOICES_PER_CARD - 1) {
    const d = POOL[Math.floor(Math.random() * POOL.length)]!;
    if (d !== answer) distractors.add(d);
  }
  const choices = shuffle([answer, ...distractors]);
  return { answer, choices };
}

function shuffle<T>(input: readonly T[]): T[] {
  const arr = [...input];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const ai = arr[i]!;
    const aj = arr[j]!;
    arr[i] = aj;
    arr[j] = ai;
  }
  return arr;
}

export function Flashcards({ onNavigate }: FlashcardsProps) {
  const [sessionMode, setSessionMode] = useState<SessionMode | null>(null);
  if (!sessionMode) {
    return (
      <Picker
        onStart={setSessionMode}
        onBack={() => onNavigate("/play/day-shapes")}
      />
    );
  }
  return (
    <Game
      sessionMode={sessionMode}
      onAgain={() => setSessionMode(sessionMode)}
      onBack={() => onNavigate("/play/day-shapes")}
    />
  );
}

function Picker({
  onStart,
  onBack,
}: {
  onStart: (m: SessionMode) => void;
  onBack: () => void;
}) {
  return (
    <>
      <h2>Identify the day shape</h2>
      <p className="muted">
        Each card shows a black-silhouette day shape arrangement. Pick
        the vessel it identifies from four choices.
      </p>
      <div className="card">
        <h3>Session length</h3>
        <div
          className="row"
          style={{ flexDirection: "column", gap: 8, alignItems: "stretch" }}
        >
          <button onClick={() => onStart("cards")}>
            {CARDS_PER_SESSION} cards
          </button>
          <button className="secondary" onClick={() => onStart("timed")}>
            Beat the clock — {Math.round(BEAT_THE_CLOCK_MS / 1000)} seconds
          </button>
        </div>
      </div>
      <button className="secondary" onClick={onBack}>
        ← Back to Day shapes
      </button>
    </>
  );
}

interface GameProps {
  sessionMode: SessionMode;
  onAgain: () => void;
  onBack: () => void;
}

function Game({ sessionMode, onAgain, onBack }: GameProps) {
  const recorder = useMemo(
    () =>
      new SessionRecorder({
        module: MODULE,
        game: GAME,
        length: sessionMode === "cards" ? CARDS_PER_SESSION : 0,
      }),
    [sessionMode],
  );

  const recentRef = useRef<string[]>([]);
  const [index, setIndex] = useState(0);
  const [card, setCard] = useState<Card>(() => {
    const c = nextCard([]);
    recentRef.current = [c.answer];
    return c;
  });
  const [shownAt, setShownAt] = useState<number>(() => Date.now());
  const [feedback, setFeedback] = useState<"right" | "wrong" | null>(null);
  const [result, setResult] = useState<SessionResult | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const sessionStartedAt = useRef<number>(Date.now());

  const [now, setNow] = useState<number>(Date.now());
  useEffect(() => {
    if (sessionMode !== "timed" || result) return;
    const t = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(t);
  }, [sessionMode, result]);
  const timeLeftMs = Math.max(
    0,
    BEAT_THE_CLOCK_MS - (now - sessionStartedAt.current),
  );
  useEffect(() => {
    if (sessionMode !== "timed" || result) return;
    if (timeLeftMs === 0) setResult(recorder.summarise());
  }, [timeLeftMs, sessionMode, result, recorder]);

  const advance = useCallback(() => {
    if (sessionMode === "cards" && index + 1 >= CARDS_PER_SESSION) {
      setResult(recorder.summarise());
      return;
    }
    setIndex((i) => i + 1);
    const next = nextCard(recentRef.current);
    recentRef.current = [...recentRef.current, next.answer].slice(-AVOID_WINDOW);
    setCard(next);
    setShownAt(Date.now());
    setFeedback(null);
  }, [index, recorder, sessionMode]);

  const onChoose = useCallback(
    (choice: string) => {
      if (feedback || result) return;
      const ms = Date.now() - shownAt;
      const ok = choice === card.answer;
      const attempt: Omit<Attempt, "module"> = {
        char: card.answer,
        ok,
        ms,
        ts: Date.now(),
      };
      recorder.log(attempt);
      recordAttempt({ ...attempt, module: MODULE });
      setFeedback(ok ? "right" : "wrong");
      setTimeout(advance, ok ? 1500 : 3500);
    },
    [advance, card, feedback, recorder, result, shownAt],
  );

  if (result) {
    return <EndCard result={result} onAgain={onAgain} onBack={onBack} />;
  }

  const liveCorrect = recorder.summarise().correct;
  const header =
    sessionMode === "cards"
      ? `Card ${index + 1} of ${CARDS_PER_SESSION}`
      : `${Math.ceil(timeLeftMs / 1000)} s left · ${liveCorrect} right`;

  const vessel = NAV_LIGHTS[card.answer]!;

  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <HelpButton onOpen={() => setHelpOpen(true)} />
      </div>
      <HelpPanel section={helpOpen ? "day-shapes" : null} onClose={() => setHelpOpen(false)} />
      <p className="small muted">{header}</p>
      <div className="card" style={{ textAlign: "center" }}>
        <DayShapeFigure
          shapes={vessel.dayShapes}
          {...(vessel.dayShapesObstructionSide
            ? { obstructionSide: vessel.dayShapesObstructionSide }
            : {})}
          {...(vessel.dayShapesSafeSide
            ? { safeSide: vessel.dayShapesSafeSide }
            : {})}
          size={200}
        />
        {feedback && (
          <div
            style={{
              marginTop: 8,
              padding: 8,
              borderRadius: 6,
              background:
                feedback === "right"
                  ? "rgba(44, 122, 59, 0.10)"
                  : "rgba(184, 48, 42, 0.10)",
              textAlign: "left",
            }}
          >
            <p style={{ margin: 0, fontWeight: 600 }}>
              {feedback === "right" ? "✓ " : "✗ "}
              {vessel.name}
            </p>
            <p className="small muted" style={{ margin: "4px 0 0" }}>
              <strong>Rule:</strong> {vessel.dayShapeRule}
            </p>
          </div>
        )}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 8,
            marginTop: 16,
          }}
        >
          {card.choices.map((id) => (
            <button
              key={id}
              className="secondary"
              disabled={!!feedback}
              onClick={() => onChoose(id)}
              style={{
                fontSize: 14,
                padding: "12px 8px",
                textAlign: "left",
                background:
                  feedback && id === card.answer
                    ? "rgba(44, 122, 59, 0.18)"
                    : undefined,
              }}
            >
              {NAV_LIGHTS[id]?.name ?? id}
            </button>
          ))}
        </div>
      </div>
    </>
  );
}

function EndCard({
  result,
  onAgain,
  onBack,
}: {
  result: SessionResult;
  onAgain: () => void;
  onBack: () => void;
}) {
  const rolling = POOL
    .map((id) => ({ id, ...statsFor(MODULE, id) }))
    .filter((s) => s.attempts >= 3 && s.hitRate < 1)
    .sort((a, b) => a.hitRate - b.hitRate);
  const weakest = rolling.slice(0, 5);

  return (
    <>
      <h2>Session complete</h2>
      <div className="card">
        <p>
          <strong>{result.correct}</strong> / {result.total} correct
          {result.total > 0 && (
            <> ({Math.round((result.correct / result.total) * 100)}%)</>
          )}
          .
        </p>
      </div>
      {weakest.length > 0 && (
        <div className="card">
          <h3>Weakest (rolling)</h3>
          <ul className="small muted">
            {weakest.map((w) => (
              <li key={w.id}>
                {NAV_LIGHTS[w.id]?.name ?? w.id} —{" "}
                {Math.round(w.hitRate * 100)}% over {w.attempts} attempts
              </li>
            ))}
          </ul>
        </div>
      )}
      <div
        className="row"
        style={{ gap: 8, flexDirection: "column", alignItems: "stretch" }}
      >
        <button onClick={onAgain}>Play another</button>
        <button className="secondary" onClick={onBack}>
          ← Back to Day shapes
        </button>
      </div>
    </>
  );
}
